"use client";

import { useEffect } from "react";

import {
  reportWebVitals,
  WebVitalsMetric,
  getMetricRating,
} from "@/utils/performance";

export function WebVitals() {
  useEffect(() => {
    // Dynamically import web-vitals to avoid SSR issues
    import("web-vitals")
      .then(({ onCLS, onFCP, onLCP, onTTFB, onINP }) => {
        const handleMetric = (metric: any) => {
          const webVitalsMetric: WebVitalsMetric = {
            ...metric,
            rating: getMetricRating(metric.name, metric.value),
          };

          reportWebVitals(webVitalsMetric);
        };

        // Monitor Core Web Vitals
        onCLS(handleMetric);
        onFCP(handleMetric);
        onLCP(handleMetric);
        onTTFB(handleMetric);
        onINP(handleMetric);
      })
      // eslint-disable-next-line no-console
      .catch(console.error);
  }, []);

  // This component doesn't render anything
  return null;
}

// Analytics integration component
export function Analytics() {
  useEffect(() => {
    // Google Search Console verification — kept immediate (a tiny meta tag
    // that must be present when Google checks for verification).
    if (process.env.NEXT_PUBLIC_GSC_VERIFICATION) {
      const meta = document.createElement("meta");

      meta.name = "google-site-verification";
      meta.content = process.env.NEXT_PUBLIC_GSC_VERIFICATION;
      document.head.appendChild(meta);
    }

    // Google Analytics 4 — deferred to browser idle so the ~160 KB gtag.js
    // loads OFF the critical path. It was previously injected in this effect
    // (right after hydration), competing with the hero image + first paint.
    if (process.env.NEXT_PUBLIC_GA_ID && typeof window !== "undefined") {
      const loadGA = () => {
        const script = document.createElement("script");

        script.src = `https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GA_ID}`;
        script.async = true;
        document.head.appendChild(script);

        window.gtag =
          window.gtag ||
          function () {
            ((window.gtag as any).q = (window.gtag as any).q || []).push(
              arguments
            );
          };
        window.gtag("js", new Date());
        window.gtag("config", process.env.NEXT_PUBLIC_GA_ID, {
          page_title: document.title,
          page_location: window.location.href,
        });
      };

      const ric = (window as any).requestIdleCallback;

      if (typeof ric === "function") {
        ric(loadGA, { timeout: 4000 });
      } else {
        setTimeout(loadGA, 3000);
      }
    }
  }, []);

  return null;
}

// Performance monitoring component for development
export function PerformanceMonitor() {
  useEffect(() => {
    if (
      process.env.NODE_ENV === "development" &&
      process.env.NEXT_PUBLIC_DEBUG_PERFORMANCE === "true"
    ) {
      import("@/utils/performance").then(
        ({ PerformanceOptimizer, bundleOptimization }) => {
          const optimizer = PerformanceOptimizer.getInstance();

          // Monitor performance metrics in development
          optimizer.monitorLCP();
          optimizer.monitorCLS();
          optimizer.monitorResources();

          // Monitor bundle and chunk loading
          bundleOptimization.analyzeBundleSize();
          bundleOptimization.monitorChunkLoading();

          // Log navigation metrics
          setTimeout(() => {
            const metrics = optimizer.getNavigationMetrics();

            if (metrics) {
              // eslint-disable-next-line no-console
              console.log("[Performance] Navigation Metrics:");
              // eslint-disable-next-line no-console
              console.table(metrics);
            }
          }, 2000);

          // Cleanup on unmount
          return () => optimizer.disconnect();
        }
      );
    }
  }, []);

  return null;
}

// Enhanced performance debug component
export function PerformanceDebugger() {
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_DEBUG_PERFORMANCE === "true") {
      // Log performance timing information
      const logPerformanceInfo = () => {
        if (typeof window !== "undefined" && window.performance) {
          const navigation = performance.getEntriesByType(
            "navigation"
          )[0] as PerformanceNavigationTiming;
          const paint = performance.getEntriesByType("paint");

          // eslint-disable-next-line no-console
          console.log("[Performance Debug] Page Load Info:", {
            domContentLoaded:
              navigation.domContentLoadedEventEnd -
              navigation.domContentLoadedEventStart,
            loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
            firstPaint: paint.find(p => p.name === "first-paint")?.startTime,
            firstContentfulPaint: paint.find(
              p => p.name === "first-contentful-paint"
            )?.startTime,
          });
        }
      };

      // Log after page load
      if (document.readyState === "complete") {
        logPerformanceInfo();
      } else {
        window.addEventListener("load", logPerformanceInfo);
      }

      return () => {
        window.removeEventListener("load", logPerformanceInfo);
      };
    }
  }, []);

  return null;
}

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}
